// Initialize CascadeAnimations only if it has not been initialized yet
if (!window.theme || !window.theme.cascadeAnimationsInitialized) {
  class CascadeAnimations {
    // Map function to scale values
    map(number, in_min, in_max, out_min, out_max) {
      return ((number - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min;
    }

    // Set and return position change
    setPosition() {
      let oldPosition = this.position;
      this.position = (document.documentElement || document.body.parentNode || document.body).scrollTop || window.pageYOffset;
      return oldPosition !== this.position;
    }

    // Calculate position update value
    updatePosition(percentage, speed) {
      let value = speed * (100 * (1 - percentage));
      return Math.round(value);
    }

    // Cache parallax containers
    cacheParallaxContainers() {
      for (let i = 0; i < this.parallaxContainers.length; i++) {
        let item = this.createParallaxItem(this.parallaxContainers[i]);
        this.parallaxItems.push(item);
      }
    }

    // Check if element is in viewport
    inViewport(element) {
      if (!element) return false;
      if (element.nodeType !== 1) return false;
      let rect = element.getBoundingClientRect();
      return rect.width > 0 && rect.height > 0 && rect.bottom >= 0 && rect.right >= 0 && rect.left <= window.innerWidth && rect.top <= window.innerHeight;
    }

    // Create parallax item object
    createParallaxItem(el) {
      const id = el.getAttribute('data-parallax-id');
      const container = el;
      const item = el.querySelector('[data-parallax-element]');
      let speed = parseInt(el.getAttribute('data-parallax-speed'), 10);
      speed *= -1;

      const blockHeight = item.clientHeight || item.offsetHeight || item.scrollHeight;
      const isInViewport = this.inViewport(el);

      return {
        id: id,
        container: container,
        item: item,
        height: blockHeight,
        speed: speed,
        visible: isInViewport
      };
    }

    // Observe and handle cascade items
    observeCascadeItems(enableParallax, enableFadeIn) {
      if (enableParallax) {
        this.parallaxObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (this.enableParallax) {
              const parallaxItemIndex = this.parallaxItems.findIndex(item => item.id === entry.target.getAttribute('data-parallax-id'));
              if (parallaxItemIndex > -1) {
                this.parallaxItems[parallaxItemIndex].visible = entry.isIntersecting;
              }
            }
          });
        }, {
          rootMargin: '0px 0px 20% 0px',
          threshold: 0
        });
      }

      if (enableFadeIn) {
        this.revealObserver = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.intersectionRatio !== 0) {
              entry.target.classList.add('revealed');
              setTimeout(() => {
                entry.target.classList.add('revealed-complete');
              }, 500);
            }
          });
        }, {
          rootMargin: '0px 0px -20% 0px',
          threshold: 0
        });
      }

      this.cascadeItems.forEach(item => {
        if (enableParallax) {
          this.parallaxObserver.observe(item);
        }
        if (enableFadeIn) {
          this.revealObserver.observe(item);
        }
      });
    }

    // Animate parallax elements
    animate() {
      this.parallaxItems.forEach(parallaxItem => {
        if (parallaxItem.visible) {
          const scrollPercentage = (this.screenHeight - parallaxItem.item.getBoundingClientRect().top) / (this.screenHeight + parallaxItem.height) - 0.5;
          const baseValue = this.intensity * (parallaxItem.speed * (scrollPercentage * 100));
          const valueY = Math.round(baseValue * 100 + Number.EPSILON) / 100;
          parallaxItem.item.style.transform = `translateY(${valueY}px)`;
        }
      });
      this.firstAnimate = true;
    }

    // Add animated classes to elements
    addAnimatedClasses() {
      this.parallaxItems.forEach(parallaxItem => {
        parallaxItem.item.classList.add('parallaxed');
      });
    }

    // Initialize parallax settings
    initParallax() {
      this.screenHeight = window.innerHeight;
      this.parallaxItems = [];
      this.parallaxContainers = document.querySelectorAll('[data-parallax-container]');
      this.setPosition();
      this.cacheParallaxContainers();
      this.intensity = this.map(window.theme.settings.cascade_parallax_intensity, 0, 100, 1, 110) / 100;
      this.scheduleAnimate();
      this.addAnimatedClasses();
    }

    // Schedule animation updates
    scheduleAnimate() {
      const animate = () => {
        if (this.setPosition()) {
          requestAnimationFrame(this.animate.bind(this));
        }
      };

      document.addEventListener('scroll', animate, { passive: true });
      window.addEventListener('resize', debounce(() => {
        this.screenHeight = window.innerHeight;
        this.animate();
      }, 200), { passive: true });
    }

    // Initialize the CascadeAnimations instance
    init() {
      this.enableParallax = window.theme.settings.cascade_enable_parallax;
      this.enableFadeIn = window.theme.settings.cascade_fade_in_items;
      this.cascadeItems = document.querySelectorAll('[data-cascade-item]');
      this.observeCascadeItems(this.enableParallax, this.enableFadeIn);
      if (this.enableParallax) {
        this.initParallax();
      }
      window.addEventListener('resize', () => {
        if (this.enableParallax) {
          this.initParallax();
        }
      });
      window.addEventListener('cascade:section:hasmutated', debounce(() => {
        this.init();
      }, 300));
    }
  }

  const cascadeAnimations = new CascadeAnimations();

  if (window.theme.settings.cascade_enable_parallax || window.theme.settings.cascade_fade_in_items) {
    if (window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
      document.addEventListener('DOMContentLoaded', () => {
        cascadeAnimations.init();
      });

      document.addEventListener('shopify:section:load', () => {
        cascadeAnimations.init();
      });

      const mutationHandler = debounce(() => {
        cascadeAnimations.init();
      }, 500);

      document.addEventListener('dev:hotreloadmutation', mutationHandler);
    }
    cascadeAnimations.init();
  }

  window.theme.cascadeAnimationsInitialized = true;
}
